import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { PermissionRole, ResourceType } from '@dealstack/shared/lib/generated';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import {
	createMIPBankAccountData,
	deleteMIPBankAccountData,
	updateMIPBankAccountData,
	validateMIPBankAccount,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import type { DataBankAccountTct } from '@dealstack/shared/lib/generated';
import type { BankAccountVerifyFn } from '@dealstack/frontend/shared/components/BankAccountForm/useBankAccountForm.types.ts';
import { toBankAccountPayload } from '@dealstack/frontend/shared/components/BankAccountForm/BankAccountForm.utils.ts';
import type { BankAccountFormValues } from '@dealstack/frontend/shared/components/BankAccountForm/types.ts';

export const useProgrammeBankAccount = (bankAccount?: DataBankAccountTct) => {
	const { mipId: programmeId } = useRequiredParams('mipId');
	const queryClient = useQueryClient();

	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [isDeleteOpen, setIsDeleteOpen] = useState(false);
	// Draft shell created lazily on the first IBAN lookup; the final save updates it.
	const shellBankAccountIdRef = useRef<string | null>(null);

	const { hasPermission: hasEditPermission } = usePermissionRequest({
		resourceType: ResourceType.PROGRAMME,
		resourceId: programmeId,
		permissionRole: PermissionRole.EDITOR,
	});

	const refetchBankAccount = () =>
		queryClient.invalidateQueries({ queryKey: [API_PATHS.MIP_BANK_ACCOUNT_GET] });

	const closeDrawer = () => {
		setIsDrawerOpen(false);
		// The shell row stays — it carries this attempt's lookup log. Only the local
		// handle is dropped; the next save resolves the programme's shell server-side.
		shellBankAccountIdRef.current = null;
		void refetchBankAccount();
	};

	const createBankAccountMutation = useMutation({
		mutationFn: (variables: Parameters<typeof createMIPBankAccountData>[0]) =>
			createMIPBankAccountData(variables),
		onSuccess: () => {
			closeDrawer();
		},
	});
	const updateBankAccountMutation = useMutation({
		mutationFn: (variables: Parameters<typeof updateMIPBankAccountData>[0]) =>
			updateMIPBankAccountData(variables),
		onSuccess: () => {
			closeDrawer();
		},
	});
	const deleteBankAccountMutation = useMutation({
		mutationFn: (variables: Parameters<typeof deleteMIPBankAccountData>[0]) =>
			deleteMIPBankAccountData(variables),
		onSuccess: async () => {
			await refetchBankAccount();
			setIsDeleteOpen(false);
		},
	});

	const verifyIdentifiers: BankAccountVerifyFn = async (dto, { countryCode }) => {
		let accountId = bankAccount?.id ?? shellBankAccountIdRef.current;
		if (!accountId) {
			const created = await createMIPBankAccountData({
				countryCode: countryCode ?? undefined,
			});
			accountId = created.payload?.id ?? null;
			if (!accountId) {
				throw new Error('Failed to create the bank account');
			}
			shellBankAccountIdRef.current = accountId;
		}
		return await validateMIPBankAccount({ bankAccountId: accountId, body: dto });
	};

	const onFormSubmit = (formValues: BankAccountFormValues, bankAccountId?: string) => {
		const body = toBankAccountPayload(formValues);

		if (!body) {
			return;
		}

		const effectiveId = bankAccountId ?? shellBankAccountIdRef.current;
		if (effectiveId) {
			updateBankAccountMutation.mutate({ body, params: { bankAccountId: effectiveId } });
			return;
		}

		createBankAccountMutation.mutate(body);
	};

	return {
		hasEditPermission,
		verifyIdentifiers,
		isDrawerOpen,
		openDrawer: () => setIsDrawerOpen(true),
		closeDrawer,
		isDeleteOpen,
		openDelete: () => setIsDeleteOpen(true),
		closeDelete: () => setIsDeleteOpen(false),
		onFormSubmit,
		isRequestLoading:
			createBankAccountMutation.isPending || updateBankAccountMutation.isPending,
		deleteBankAccount: (bankAccountId: string) =>
			deleteBankAccountMutation.mutate({ bankAccountId }),
		isDeleteLoading: deleteBankAccountMutation.isPending,
		isDeleteSuccess: deleteBankAccountMutation.isSuccess,
	};
};
